import { Autorenew, CheckCircleOutlined, DescriptionOutlined, ErrorOutlined, RemoveCircleOutlined, SkipNext } from "@mui/icons-material"
import type { SvgIconComponent } from "@mui/icons-material"
import type { RunErrorType, RunOutcome, ScheduledWorkflowLastRun, ScheduledWorkflowLatestDeliverable } from "resources/scheduled-workflows/types"

export type LatestReportState = "running" | "report" | "quiet" | "failed" | "skipped" | "none"

export interface LatestReport {
  state: LatestReportState
  time: string | null
}

interface RunOutcomeDisplay {
  color: string
  icon: SvgIconComponent
}

export const RUN_OUTCOME_DISPLAY: Record<LatestReportState, RunOutcomeDisplay> = {
  running: { color: "info.main", icon: Autorenew },
  report: { color: "text.primary", icon: DescriptionOutlined },
  quiet: { color: "text.secondary", icon: CheckCircleOutlined },
  failed: { color: "error.main", icon: ErrorOutlined },
  skipped: { color: "warning.main", icon: SkipNext },
  none: { color: "text.disabled", icon: RemoveCircleOutlined },
}

const SKIPPED_ERROR_TYPE: RunErrorType = "session_rate_limited_error"

export function latestReportOf(lastRun: ScheduledWorkflowLastRun | null, latestDeliverable: ScheduledWorkflowLatestDeliverable | null): LatestReport {
  if (lastRun == null) return { state: "none", time: null }
  const time = lastRun.createdAt
  if (lastRun.status === "running") return { state: "running", time }
  if (lastRun.status === "not_started") return { state: lastRun.errorType === SKIPPED_ERROR_TYPE ? "skipped" : "failed", time }
  if (lastRun.outcome == null) return { state: "running", time }
  if (lastRun.outcome === "succeeded" && latestDeliverable != null) return { state: "report", time: latestDeliverable.createdAt }
  if (lastRun.outcome === "succeeded" || lastRun.outcome === "succeeded_empty") return { state: "quiet", time }
  return { state: "failed", time }
}

const PARTIAL_OUTCOME_WORDS: Partial<Record<RunOutcome, string>> = {
  failed: "Failed",
  stalled: "Stalled",
  unknown: "Unknown",
}

export function runOutcomeWord(outcome: RunOutcome | null): string | null {
  if (outcome === "succeeded_empty") return "Quiet"
  return outcome != null && Object.hasOwn(PARTIAL_OUTCOME_WORDS, outcome) ? PARTIAL_OUTCOME_WORDS[outcome] ?? null : null
}

export function latestReportLabel({ state, time }: LatestReport, formatTime: (time: string) => string): string {
  if (state === "running") return "Running now…"
  if (state === "none" || time == null) return "No runs yet"
  const formattedTime = formatTime(time)
  if (state === "quiet") return `Quiet · ${formattedTime}`
  if (state === "failed") return `Failed · ${formattedTime}`
  if (state === "skipped") return `Skipped · ${formattedTime}`
  return formattedTime
}

const FAILED_TOOLTIPS: Partial<Record<RunErrorType, string>> = {
  vault_not_found_error: "Failed — a connection the workflow needs is missing.",
  environment_archived_error: "Failed — the workspace the workflow runs in was removed.",
  agent_archived_error: "Failed — the agent the workflow uses was removed.",
  budget_reached: "Failed — the run reached its usage limit.",
  retries_exhausted: "Failed — the run kept hitting errors and stopped.",
  refusal: "Failed — Kelly declined to finish the run.",
  agent_reported: "Failed — Kelly reported a problem during the run.",
}

const FAILED_FALLBACK_TOOLTIP = "Failed — something went wrong. Run now to retry."

export function failedTooltip(errorType: RunErrorType | null): string {
  return errorType != null && Object.hasOwn(FAILED_TOOLTIPS, errorType) ? FAILED_TOOLTIPS[errorType] ?? FAILED_FALLBACK_TOOLTIP : FAILED_FALLBACK_TOOLTIP
}
